import React, { useEffect } from 'react';
import queryString from 'query-string';
import ParentLayout from '@/pages/partners-filtered/Layout';
import useRouter from '@/hooks/useRouter';
import User from '@/pages/_common_user/ManageUser';
import useQuery from './useQuery';
import DateJoined from '@/pages/_common_user/User/DateJoined';
import { DialogManager } from '@/dialogs_new/DialogManager';
import useDialogLauncher from '@hooks/@lore/dialogs-routable/_hooks/useDialogLauncher';
import DistanceFilter, { RADIUS_OPTIONS, toLocationCondition } from '@/components/ui/DistanceFilter';
import SortBy from '@/pages/partners/SortBy';
import getSearchParamsFromQuery from '@/utils/user/getSearchParamsFromQuery';
import {useTranslation} from "react-i18next";

// Applied when the url has no explicit value; never written to the url.
const DEFAULT_LOCATION_CONDITION = toLocationCondition(
  RADIUS_OPTIONS.find(option => option.id === 'le-50')
);
const DEFAULT_ORDER = 'distance asc';

export default function Layout() {
  const { location, history, navigate } = useRouter();
  const launch = useDialogLauncher();
  const {t} = useTranslation();

  const PAGE_SIZE = 12;

  const search = queryString.parse(location.search);

  const locationCondition = search.locationCondition ?
    getSearchParamsFromQuery(search).locationCondition :
    DEFAULT_LOCATION_CONDITION;

  // The defaults live here rather than in the url, so the query sees them even
  // though the filter controls only ever write explicit choices.
  const searchWithDefaults = {
    ...search,
    order: search.order || DEFAULT_ORDER,
    locationCondition: JSON.stringify(locationCondition)
  };

  // Exception: the sort order is written back to the url, so SortBy reflects it.
  // Replaced rather than pushed, to keep it out of the back button history.
  useEffect(() => {
    if (!search.order) {
      navigate({
        pathname: location.pathname,
        search: queryString.stringify({
          ...search,
          order: DEFAULT_ORDER
        })
      }, { replace: true });
    }
  }, [location.search]);

  const result = useQuery({
    search: searchWithDefaults,
    pageSize: PAGE_SIZE
  });

  const csvQuery = useQuery({
    search: searchWithDefaults,
    returnCsvQuery: true
  });

  function onDownload(query) {
    launch(DialogManager.CSVDownload.Config(query, { skipNetworkFilter: true }));
  }

  function onChangeLocationCondition(option) {
    const search = queryString.parse(location.search) || {};

    // Always written, including "anywhere", so an explicit choice is
    // distinguishable from the default applied when the param is absent.
    search.locationCondition = JSON.stringify(toLocationCondition(option));

    history.push({
      pathname: location.pathname,
      search: queryString.stringify({
        ...search,
        page: 1
      })
    });
  }

  return (
    <ParentLayout
      breadcrumbs={['Explore', 'Partners']}
      title={t('partners.new')}
      description={(
        <span>
          {t('partners.new_subtitle')}
        </span>
      )}
      pageSize={PAGE_SIZE}
      result={result}
      csvQuery={function () {
        onDownload(csvQuery);
      }}
      filters={(
        <div className="flex flex-wrap gap-4">
          <DistanceFilter
            selectedId={locationCondition.id}
            onChange={onChangeLocationCondition}
          />
          <SortBy withDistance={true} />
        </div>
      )}
      row={function(user) {
        return (
          <User key={user.id} user={user}>
            <div className="bg-cs-gray-50 p-4 border-t border-cs-gray-100">
              <DateJoined user={user} />
            </div>
          </User>
        );
      }}
    />
  );
}
